import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { THRESHOLD_CAP } from "@/lib/taxCredit";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtDate, fmtCAD } from "@/lib/format";
import { User as UserIcon, Heart, AirplaneTakeoff, ArrowRight, X, ArrowClockwise, FileZip, Lock, Eye, EyeSlash, MapPin, House, Calculator, CalendarPlus, Copy as CopyIcon, Ticket, Plus, Download, UsersThree, Trash, Lifebuoy } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  getFamilyMembers, addFamilyMember, removeFamilyMember, subscribeToFamilyChanges,
} from "@/lib/familyMembers";

// CRA simplified mileage rates ($/km) — kept in sync with backend PROVINCE_RATES
// Source: CRA "Meal and vehicle rates used to calculate travel expenses" — 2025
const CRA_RATES = {
  AB: 0.55, BC: 0.57, MB: 0.56, NB: 0.585, NL: 0.605, NS: 0.575,
  NT: 0.725, NU: 0.71, ON: 0.62, PE: 0.575, QC: 0.59, SK: 0.535, YT: 0.705,
};
const PROVINCES = [
  { code: "AB", name: "Alberta" }, { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" }, { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland & Labrador" }, { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" }, { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" }, { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" }, { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

export default function Settings() {
  const { user, isAuthed, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [stats, setStats] = useState(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [provinceBusy, setProvinceBusy] = useState(false);
  const [homeQuery, setHomeQuery] = useState(user?.home_address || "");
  const [homeResults, setHomeResults] = useState([]);
  const [homeBusy, setHomeBusy] = useState(false);
  const [netIncomeInput, setNetIncomeInput] = useState(user?.net_income || "");
  const homeDebRef = useRef(null);

  // Calendar subscription feed — lets the user one-tap subscribe Apple
  // Calendar / Google Calendar to their MedEasy trips. We mint a long-lived
  // opaque token on demand; the user can rotate it (which invalidates the
  // old subscribe URL) at any time.
  const [calFeedToken, setCalFeedToken] = useState(null);
  const [calFeedBusy, setCalFeedBusy] = useState(false);

  // Family Mode (MVP) — frontend-only member list stored in localStorage.
  // Drives the "Whose appointment?" dropdown on the Trip form. See
  // /lib/familyMembers.js for the storage contract and known limitations
  // (no cross-device sync, no tax-line split — beta-prototype only).
  const [familyMembers, setFamilyMembersState] = useState(getFamilyMembers());
  const [newMemberName, setNewMemberName] = useState("");

  // Redeem Code — Dave's pre-generated LIFE-/YEAR- tokens (see backend
  // /api/redeem-token). One-time-use; success extends subscription and
  // billing state auto-refreshes.
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemBusy, setRedeemBusy] = useState(false);

  useEffect(() => {
    return subscribeToFamilyChanges((list) => setFamilyMembersState(list));
  }, []);

  const handleAddMember = () => {
    const trimmed = newMemberName.trim();
    if (!trimmed) return;
    if (trimmed.length > 40) {
      toast.error("Names must be 40 characters or less.");
      return;
    }
    const existing = familyMembers.some((m) => m.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      toast.error(`${trimmed} is already in your family list.`);
      return;
    }
    const updated = addFamilyMember(trimmed);
    setFamilyMembersState(updated);
    setNewMemberName("");
    toast.success(`Added ${trimmed} — they'll appear in the "Whose appointment?" dropdown on every trip.`);
  };

  const handleRemoveMember = (name) => {
    if (name.toLowerCase() === "self") return; // Defensive — UI already hides the X for Self
    const updated = removeFamilyMember(name);
    setFamilyMembersState(updated);
    toast.success(`Removed ${name}. (Past trips tagged to ${name} keep their label.)`);
  };

  useEffect(() => {
    if (!isAuthed) return;
    api.get("/calendar/feed-token").then((r) => setCalFeedToken(r.data?.token || null)).catch(() => {});
  }, [isAuthed]);

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const calHttpsUrl = calFeedToken ? `${backendBase}/api/calendar/feed.ics?token=${calFeedToken}` : "";
  // webcal:// is Apple/Google's "subscribe to calendar feed" scheme. Tapping
  // it on iPhone pops the system "Subscribe to Calendar?" sheet, after
  // which Calendar.app polls the feed forever and every new trip the user
  // logs auto-syncs — including the 2-day reminder.
  const calWebcalUrl = calHttpsUrl
    ? calHttpsUrl.replace(/^https?:\/\//, "webcal://")
    : "";

  const subscribeCalendar = async () => {
    setCalFeedBusy(true);
    try {
      let token = calFeedToken;
      if (!token) {
        const { data } = await api.post("/calendar/feed-token");
        token = data.token;
        setCalFeedToken(token);
      }
      const url = `${backendBase}/api/calendar/feed.ics?token=${token}`.replace(/^https?:\/\//, "webcal://");
      // iOS: this triggers the native "Subscribe to Calendar?" sheet.
      // Desktop: macOS Calendar.app intercepts webcal://; Windows asks the
      // user which calendar app to open.
      window.location.href = url;
    } catch {
      toast.error("Couldn't set up the calendar feed — try again.");
    } finally {
      setTimeout(() => setCalFeedBusy(false), 1500);
    }
  };

  const rotateCalendarToken = async () => {
    if (!window.confirm("Rotate your calendar subscription URL? Existing subscriptions will stop syncing — you'll need to re-subscribe.")) return;
    const { data } = await api.post("/calendar/feed-token");
    setCalFeedToken(data.token);
    toast.success("New subscription URL minted.");
  };

  const copyCalendarUrl = async () => {
    if (!calHttpsUrl) return;
    try {
      await navigator.clipboard.writeText(calHttpsUrl);
      toast.success("Calendar URL copied — paste into Google Calendar / Outlook → 'Add by URL'");
    } catch {
      toast.error("Couldn't copy — long-press the link below to copy manually");
    }
  };

  // Comp codes (owner-only). Mint redeemable 365-day codes to give to
  // advocacy partners (Crohn's & Colitis Foundation, Caregivers Assoc.).
  const [compSummary, setCompSummary] = useState([]); // [{label,total,redeemed,remaining}, ...]
  const [compCodes, setCompCodes] = useState([]);     // full list (for CSV)
  const [compForm, setCompForm] = useState({ count: 10, days: 365, label: "CROHNS" });
  const [compBusy, setCompBusy] = useState(false);

  const refreshComp = async () => {
    try {
      const { data } = await api.get("/admin/comp-codes");
      setCompSummary(data?.summary || []);
      setCompCodes(data?.codes || []);
    } catch {
      // 403 if not owner — silently leave the section empty
    }
  };

  useEffect(() => {
    if (isAuthed && billing?.is_owner) refreshComp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, billing?.is_owner]);

  const mintCompCodes = async () => {
    setCompBusy(true);
    try {
      const count = Math.max(1, Math.min(200, parseInt(compForm.count) || 1));
      const days = Math.max(1, Math.min(36500, parseInt(compForm.days) || 365));
      const label = (compForm.label || "PARTNER").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 24) || "PARTNER";
      const { data } = await api.post("/admin/comp-codes", { count, days, label });
      toast.success(`Minted ${data.minted.length} ${label} code${data.minted.length === 1 ? "" : "s"}`);
      await refreshComp();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mint failed");
    } finally {
      setCompBusy(false);
    }
  };

  const downloadCompCsv = (label) => {
    const rows = compCodes.filter((c) => !label || c.label === label);
    if (!rows.length) return;
    const header = ["code", "label", "days", "created_at", "redeemed", "redeemed_by_email", "redeemed_at"];
    const csv = [header.join(",")].concat(
      rows.map((r) => header.map((h) => JSON.stringify(r[h] ?? "")).join(","))
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `medeasy-comp-codes-${(label || "all").toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 250);
  };

  useEffect(() => { setHomeQuery(user?.home_address || ""); }, [user?.home_address]);
  useEffect(() => { setNetIncomeInput(user?.net_income || ""); }, [user?.net_income]);

  const searchHomeAddress = (q) => {
    clearTimeout(homeDebRef.current);
    if (!q || q.length < 3 || q === user?.home_address) { setHomeResults([]); return; }
    homeDebRef.current = setTimeout(async () => {
      setHomeBusy(true);
      try {
        const { data } = await api.get(`/geocode?q=${encodeURIComponent(q)}`);
        setHomeResults(data?.matches || []);
      } catch { setHomeResults([]); }
      finally { setHomeBusy(false); }
    }, 350);
  };

  const pickHomeAddress = async (m) => {
    setHomeBusy(true);
    try {
      await api.patch("/auth/settings", { home_address: m.display_name, home_lat: m.lat, home_lng: m.lng });
      await refresh();
      setHomeResults([]);
      setHomeQuery(m.display_name);
      toast.success("Home address saved — stored on your phone. Never shared with MedEasy.");
    } catch {
      toast.error("Could not save home address");
    } finally { setHomeBusy(false); }
  };

  const saveNetIncome = async () => {
    const v = parseFloat(netIncomeInput);
    if (!v || v <= 0) { toast.error("Enter a positive net income"); return; }
    try {
      await api.patch("/auth/settings", { net_income: v });
      await refresh();
      toast.success(`Net income saved — threshold updates automatically`);
    } catch { toast.error("Could not save net income"); }
  };

  const handleProvinceChange = async (newProvince) => {
    if (!isAuthed || newProvince === user?.province) return;
    setProvinceBusy(true);
    try {
      await api.patch("/auth/settings", { province: newProvince });
      await refresh();
      toast.success(`Province updated to ${newProvince} — new trips will use $${CRA_RATES[newProvince].toFixed(3)}/km`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update province");
    } finally {
      setProvinceBusy(false);
    }
  };

  const refreshBilling = () => {
    if (isAuthed) api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
  };

  useEffect(() => {
    refreshBilling();
    api.get("/billing/stats").then((r) => setStats(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const handleCancel = async () => {
    setCancelBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Subscription cancelled. You'll keep access until your current period ends.");
      setConfirmingCancel(false);
      refreshBilling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel. Please try again.");
    } finally {
      setCancelBusy(false);
    }
  };

  const handleResume = async () => {
    setResumeBusy(true);
    try {
      await api.post("/billing/resume");
      toast.success("Welcome back! Your subscription will continue renewing.");
      refreshBilling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume. Please try again.");
    } finally {
      setResumeBusy(false);
    }
  };

  const handleRedeemToken = async () => {
    const code = redeemCode.trim().toUpperCase();
    if (!code) {
      toast.error("Enter your code first");
      return;
    }
    setRedeemBusy(true);
    try {
      const { data } = await api.post("/redeem-token", { code });
      toast.success(`Code redeemed! Your ${data.tier_label} access is now active.`);
      setRedeemCode("");
      refreshBilling();
      try { await refresh?.(); } catch { /* auth context refresh is best-effort */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem that code");
    } finally {
      setRedeemBusy(false);
    }
  };

  const [zipBusy, setZipBusy] = useState(false);
  const [zipPassword, setZipPassword] = useState("");
  const [showZipPwd, setShowZipPwd] = useState(false);

  const handleDownloadReceiptsZip = async () => {
    if (zipPassword && zipPassword.length < 8) {
      toast.error("Password must be at least 8 characters (or leave blank for a plain ZIP)");
      return;
    }
    setZipBusy(true);
    try {
      const res = await api.get("/files/export-all", {
        responseType: "blob",
        params: zipPassword ? { password: zipPassword } : undefined,
      });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const fname = (m && m[1]) || `medeasy_receipts_${new Date().toISOString().slice(0, 10)}.zip`;
      const blob = new Blob([res.data], { type: "application/zip" });
      const a = document.createElement("a");
      const url = URL.createObjectURL(blob);
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success(zipPassword ? "Encrypted ZIP downloaded — keep your password safe" : "Receipt archive downloaded");
      setZipPassword("");
    } catch (e) {
      const status = e?.response?.status;
      let msg = "Could not export receipts. Try again in a minute.";
      if (status === 404) msg = "You haven't uploaded any receipts yet";
      else if (status === 400) {
        // Parse JSON error from blob response (axios responseType is blob)
        try {
          const text = await e.response.data.text();
          const data = JSON.parse(text);
          msg = data.detail || msg;
        } catch { /* keep default msg */ }
      }
      toast.error(msg);
    } finally {
      setZipBusy(false);
    }
  };

  const goLogin = () => navigate("/login?next=/settings");

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#5C6660] font-semibold">Configuration</div>
        <h1 className="font-[Manrope] text-4xl font-bold tracking-tight mt-2">Settings</h1>
        <p className="text-[#5C6660] mt-2">Account, subscription, and Hope Air contribution.</p>
      </div>

      {/* Family & Dependents — MOVED to the very top of Settings so it's
          impossible to miss for caregivers. Was previously buried as the
          3rd card from the top which beta testers couldn't find. */}
      {isAuthed && (
        <Card
          className="rounded-2xl border-2 border-[#2C4C3B]/30 bg-gradient-to-br from-[#EFF4EE] to-[#F7F6F2] shadow-sm"
          data-testid="settings-family-card"
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-xl bg-[#2C4C3B] flex items-center justify-center shrink-0 shadow-sm">
                <UsersThree weight="fill" size={22} className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#2C4C3B] font-bold">New</div>
                <h2 className="font-[Manrope] font-bold text-xl text-[#1E2420]">Family &amp; Dependents</h2>
              </div>
            </div>
            <p className="text-sm text-[#5C6660] mb-4 leading-relaxed">
              Caring for a parent, child, or spouse? Add their names below — every trip you log will let you pick whose appointment it was, so the right person&apos;s expenses end up on the right CRA line at tax time.
            </p>

            <div className="space-y-2 mb-4" data-testid="settings-family-list">
              {familyMembers.map((name) => {
                const isSelf = name.toLowerCase() === "self";
                return (
                  <div
                    key={name}
                    data-testid={`family-member-${name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[#E3E1D9] bg-white px-4 py-2.5"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <UserIcon weight="duotone" size={16} className="text-[#2C4C3B] shrink-0" />
                      <span className="font-medium text-sm truncate">{name}</span>
                      {isSelf && (
                        <span className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold shrink-0">(you)</span>
                      )}
                    </div>
                    {!isSelf && (
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(name)}
                        data-testid={`family-remove-${name.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-[#5C6660] hover:text-red-600 transition-colors"
                        aria-label={`Remove ${name}`}
                      >
                        <Trash size={16} weight="duotone" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1 w-full">
                <Label htmlFor="family-new-name" className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Add a family member</Label>
                <Input
                  id="family-new-name"
                  data-testid="family-add-input"
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddMember(); } }}
                  placeholder="e.g. Mom, Dad, Avery"
                  maxLength={40}
                  className="mt-2 bg-white"
                />
              </div>
              <Button
                type="button"
                onClick={handleAddMember}
                data-testid="family-add-btn"
                className="w-full sm:w-auto rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white whitespace-nowrap shadow-sm"
                disabled={!newMemberName.trim()}
              >
                <Plus size={14} className="mr-1" /> Add member
              </Button>
            </div>

            <p className="text-xs text-[#5C6660] mt-3 italic">
              Stored in this browser only — no sync across devices yet. Full Family Mode (with cross-device sync and CRA Line 33099/33199 tax splits) coming after beta feedback.
            </p>
            {/* Extra bottom buffer so the Add button is never tucked under
                the Emergent attribution badge in preview mode (~50px tall
                at the bottom-right of the viewport). Harmless on production
                where the badge is gone — just slightly more generous
                bottom whitespace below this card. */}
            <div className="h-16 sm:h-2" aria-hidden="true" />
          </CardContent>
        </Card>
      )}

      {/* Province / mileage rate — drives the CRA $/km math on every new trip */}
      {isAuthed && (
        <Card className="rounded-2xl border-[#E3E1D9]" data-testid="settings-province-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
                <MapPin weight="fill" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="font-[Manrope] font-semibold text-lg">Your province</h2>
                <p className="text-sm text-[#5C6660]">Sets the CRA simplified mileage rate used on every new trip.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div>
                <Label htmlFor="settings-province" className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Province</Label>
                <Select
                  value={user?.province || "ON"}
                  onValueChange={handleProvinceChange}
                  disabled={provinceBusy}
                >
                  <SelectTrigger id="settings-province" data-testid="settings-province" className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVINCES.map((p) => (
                      <SelectItem key={p.code} value={p.code} data-testid={`settings-province-${p.code}`}>
                        {p.code} — {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="rounded-xl bg-[#F7F6F2] border border-[#E3E1D9] p-3" data-testid="settings-province-rate">
                <div className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">CRA simplified rate</div>
                <div className="font-[Manrope] font-bold text-2xl text-[#2C4C3B] mt-1">
                  ${(CRA_RATES[user?.province] || CRA_RATES.ON).toFixed(3)}<span className="text-sm font-normal text-[#5C6660]">/km</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-[#5C6660] mt-3 italic">
              Existing trips keep the rate they were saved with — only new trips use the updated rate.
            </p>
          </CardContent>
        </Card>
      )}

      {isAuthed && (
        <Card className="rounded-2xl border-[#E3E1D9]" data-testid="settings-home-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
                <House weight="fill" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="font-[Manrope] font-semibold text-lg">Home address</h2>
                <p className="text-sm text-[#5C6660]">Starting point for every mileage trip.</p>
              </div>
            </div>
            <div className="relative">
              <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Search address</Label>
              <Input
                data-testid="settings-home-input"
                value={homeQuery}
                onChange={(e) => { setHomeQuery(e.target.value); searchHomeAddress(e.target.value); }}
                placeholder="e.g. 123 Main St, Angusville, MB"
                autoComplete="off"
                className="mt-1"
              />
              {homeBusy && <div className="text-xs text-[#5C6660] mt-1">Searching…</div>}
              {homeResults.length > 0 && (
                <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border border-[#E3E1D9] rounded-xl shadow-lg max-h-64 overflow-y-auto">
                  {homeResults.map((m, i) => (
                    <li key={`${m.place_id}-${i}`}>
                      <button
                        type="button"
                        onClick={() => pickHomeAddress(m)}
                        data-testid={`settings-home-pick-${i}`}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[#F7F6F2] border-b border-[#E3E1D9] last:border-0"
                      >
                        <div className="font-medium truncate">{m.display_name.split(",")[0]}</div>
                        <div className="text-xs text-[#5C6660] truncate">{m.display_name}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="text-xs text-[#5C6660] mt-3 italic">
              Stored on your phone. Never shared with MedEasy.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Net income — drives dynamic 3% threshold on /mileage view */}
      {isAuthed && (
        <Card className="rounded-2xl border-[#E3E1D9]" data-testid="settings-income-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
                <Calculator weight="fill" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="font-[Manrope] font-semibold text-lg">Estimated net income</h2>
                <p className="text-sm text-[#5C6660]">Drives your CRA threshold: lesser of 3% of net income or $2,834.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Annual net income (CAD)</Label>
                <Input
                  type="number" min={0} step={100}
                  value={netIncomeInput}
                  onChange={(e) => setNetIncomeInput(e.target.value)}
                  data-testid="settings-net-income"
                  placeholder="e.g. 65000"
                  className="mt-1"
                />
              </div>
              <Button onClick={saveNetIncome} data-testid="settings-save-income" className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white h-10">
                Save
              </Button>
            </div>
            {user?.net_income > 0 && (
              <div className="mt-3 rounded-xl bg-[#F7F6F2] border border-[#E3E1D9] p-3 text-sm">
                <div className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Your CRA threshold</div>
                <div className="font-[Manrope] font-bold text-xl text-[#2C4C3B] mt-1">
                  {(Math.min(user.net_income * 0.03, THRESHOLD_CAP)).toLocaleString("en-CA", { style: "currency", currency: "CAD" })}
                </div>
                <div className="text-xs text-[#5C6660] mt-1">
                  Eligible medical expenses above this amount earn the credit.
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Calendar Sync — webcal:// subscription that auto-pushes every
          trip to Apple Calendar / Google Calendar. ONE-TAP setup, then
          MedEasy syncs your appointments forever. */}
      {isAuthed && (
        <Card className="rounded-2xl border-[#E3E1D9]" data-testid="calendar-sync-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
                <CalendarPlus weight="fill" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="font-[Manrope] font-semibold text-lg">Auto-sync to your calendar</h2>
                <p className="text-sm text-[#5C6660]">
                  One-tap subscribe. Every trip you log appears in Apple/Google Calendar with a 2-day reminder — forever.
                </p>
              </div>
            </div>

            <Button
              data-testid="calendar-subscribe-btn"
              onClick={subscribeCalendar}
              disabled={calFeedBusy}
              className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold shadow-md shadow-[#2C4C3B]/20"
            >
              <CalendarPlus weight="duotone" size={18} className="mr-2" />
              {calFeedBusy ? "Setting up…" : "Subscribe in Apple Calendar"}
            </Button>

            <p className="text-xs text-[#5C6660] mt-3 leading-relaxed">
              On iPhone, this opens iOS's <strong className="text-[#1E2420]">"Subscribe to Calendar?"</strong> sheet — tap <strong className="text-[#1E2420]">Subscribe</strong> and you're done. Every appointment auto-appears in Calendar.app. Removing a trip in MedEasy removes it from your phone too.
            </p>

            {calFeedToken && (
              <details className="mt-4 group" data-testid="calendar-advanced">
                <summary className="text-xs text-[#5C6660] cursor-pointer select-none hover:text-[#1E2420]">
                  Google Calendar / Outlook → add by URL
                </summary>
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-[#5C6660]">
                    Paste this URL into Google Calendar → <em>"Other calendars → From URL"</em> or Outlook → <em>"Add calendar → Subscribe from web"</em>:
                  </div>
                  <div className="flex items-stretch gap-2">
                    <input
                      readOnly
                      value={calHttpsUrl}
                      data-testid="calendar-feed-url"
                      onFocus={(e) => e.target.select()}
                      className="flex-1 text-xs px-3 py-2 rounded-lg border border-[#E3E1D9] bg-[#F7F6F2] font-mono text-[#1E2420] truncate"
                    />
                    <Button
                      variant="outline"
                      onClick={copyCalendarUrl}
                      data-testid="calendar-copy-url"
                      className="rounded-lg border-[#E3E1D9] shrink-0"
                    >
                      <CopyIcon size={14} className="mr-1" /> Copy
                    </Button>
                  </div>
                  <button
                    type="button"
                    onClick={rotateCalendarToken}
                    data-testid="calendar-rotate-token"
                    className="text-xs text-[#C17767] hover:underline mt-1"
                  >
                    Rotate URL (invalidates existing subscriptions)
                  </button>
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comp Codes — OWNER ONLY. Mint redeemable 365-day comp codes to
          gift to advocacy partners (Crohn's & Colitis Foundation,
          Caregivers Association) and personal beta-testers. */}
      {isAuthed && billing?.is_owner && (
        <Card className="rounded-2xl border-[#E3E1D9]" data-testid="comp-codes-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#D4A373] flex items-center justify-center">
                <Ticket weight="fill" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="font-[Manrope] font-semibold text-lg">Comp codes <span className="text-xs uppercase tracking-widest text-[#D4A373] ml-2">Owner</span></h2>
                <p className="text-sm text-[#5C6660]">
                  Mint redeemable codes for partners. Each code is one-time-use and extends the holder's subscription by the days you set.
                </p>
              </div>
            </div>

            {/* Mint form */}
            <div className="rounded-2xl border border-[#E3E1D9] bg-[#F7F6F2] p-4 space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Count</Label>
                  <Input
                    data-testid="comp-mint-count"
                    type="number" min="1" max="200"
                    value={compForm.count}
                    onChange={(e) => setCompForm({ ...compForm, count: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Days</Label>
                  <Input
                    data-testid="comp-mint-days"
                    type="number" min="1" max="36500"
                    value={compForm.days}
                    onChange={(e) => setCompForm({ ...compForm, days: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">Label</Label>
                  <Input
                    data-testid="comp-mint-label"
                    value={compForm.label}
                    onChange={(e) => setCompForm({ ...compForm, label: e.target.value })}
                    placeholder="CROHNS"
                    className="mt-1"
                  />
                </div>
              </div>
              <Button
                data-testid="comp-mint-btn"
                onClick={mintCompCodes}
                disabled={compBusy}
                className="w-full h-11 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold"
              >
                <Plus weight="bold" size={16} className="mr-2" />
                {compBusy ? "Minting…" : `Mint ${compForm.count} ${(compForm.label || "PARTNER").toUpperCase()} codes`}
              </Button>
              <p className="text-[11px] text-[#5C6660] leading-relaxed">
                Quick targets: <button type="button" onClick={() => setCompForm({ count: 10, days: 365, label: "CROHNS" })} className="underline">10 Crohn's</button> · <button type="button" onClick={() => setCompForm({ count: 12, days: 365, label: "CAREGIVERS" })} className="underline">12 Caregivers</button> · <button type="button" onClick={() => setCompForm({ count: 8, days: 365, label: "PERSONAL" })} className="underline">8 Personal</button>
              </p>
            </div>

            {/* Summary per batch */}
            {compSummary.length > 0 && (
              <div className="mt-4 space-y-2">
                {compSummary.map((s) => (
                  <div
                    key={s.label}
                    data-testid={`comp-batch-${s.label}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[#E3E1D9] p-3"
                  >
                    <div>
                      <div className="text-sm font-semibold text-[#1E2420]">{s.label}</div>
                      <div className="text-xs text-[#5C6660]">
                        {s.redeemed} of {s.total} redeemed · {s.remaining} remaining
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => downloadCompCsv(s.label)}
                      data-testid={`comp-csv-${s.label}`}
                      className="rounded-full border-[#E3E1D9]"
                    >
                      <Download size={14} className="mr-1" /> CSV
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {compCodes.length === 0 && (
              <p className="text-xs text-[#5C6660] mt-3 italic">No codes minted yet — pick a quick-target above or mint your own batch.</p>
            )}
          </CardContent>
        </Card>
      )}

      {isAuthed && (
        <Card className="rounded-2xl border-[#E3E1D9]" data-testid="settings-redeem-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#C17767] flex items-center justify-center">
                <Ticket weight="fill" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="font-[Manrope] font-semibold text-lg">Redeem code</h2>
                <p className="text-sm text-[#5C6660]">
                  Have a MedEasy lifetime or annual access code? Enter it here.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="settings-redeem-input"
                type="text"
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleRedeemToken(); } }}
                placeholder="LIFE-XXXX-XXXX or YEAR-XXXX-XXXX"
                autoComplete="off"
                spellCheck={false}
                disabled={redeemBusy}
                className="flex-1 h-11 px-3 rounded-lg border border-[#E3E1D9] bg-white text-sm font-mono tracking-wider uppercase placeholder:text-[#B7B3A5] placeholder:font-sans placeholder:tracking-normal placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-[#C17767]/40 disabled:opacity-50"
              />
              <Button
                data-testid="settings-redeem-btn"
                onClick={handleRedeemToken}
                disabled={redeemBusy}
                className="h-11 rounded-lg bg-[#C17767] hover:bg-[#AD6757] text-white px-5 font-semibold"
              >
                {redeemBusy ? "Redeeming…" : "Redeem"}
              </Button>
            </div>
            <p className="text-[11px] text-[#5C6660] mt-3 leading-relaxed">
              One-time use. LIFE-codes grant permanent lifetime access. YEAR-codes grant 365 days and stack on top of an existing subscription.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Help & Support — plain mailto so it works reliably on iOS, Android,
          and desktop. Kept as a full card (not just a footer link) so alpha
          users can find it without hunting — a real human email is one of the
          biggest trust signals for a small app collecting medical data. */}
      <a
        href="mailto:support@MyMedEasy.ca?subject=MedEasy%20support%20request"
        data-testid="settings-support-link"
        className="block group"
      >
        <Card className="rounded-2xl border-[#E3E1D9] hover:border-[#2C4C3B]/40 hover:shadow-sm transition-all">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center shrink-0">
                <Lifebuoy weight="fill" size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-[Manrope] font-semibold text-lg">Help &amp; Support</h2>
                <p className="text-sm text-[#5C6660] truncate">
                  Questions, bugs, or feature ideas — email <span className="text-[#1E2420] font-medium">support@MyMedEasy.ca</span>
                </p>
              </div>
              <ArrowRight
                size={18}
                weight="bold"
                className="text-[#5C6660] group-hover:text-[#2C4C3B] group-hover:translate-x-0.5 transition-all shrink-0"
              />
            </div>
          </CardContent>
        </Card>
      </a>

      {isAuthed && billing && (
        <Card className="rounded-2xl border-[#E3E1D9]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
                <Heart weight="fill" size={20} className="text-white" />
              </div>
              <div>
                <h2 className="font-[Manrope] font-semibold text-lg">Subscription</h2>
                <p className="text-sm text-[#5C6660]">
                  {billing.plan === "yearly" ? "Yearly plan" : billing.plan === "monthly" ? "Monthly plan" : "Auto-renews"} · 10% to Hope Air
                </p>
              </div>
            </div>

            {billing.is_owner ? (
              // OWNER — lifetime access, no Stripe needed
              <div className="rounded-xl bg-[#D4A373]/15 border border-[#D4A373]/30 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="settings-owner-banner">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#8C6D3F] font-semibold inline-flex items-center gap-1.5">
                    <Heart weight="fill" size={12} /> Lifetime · Owner
                  </div>
                  <div className="mt-1 text-sm">
                    Thank you for building MedEasy. You have lifetime access — no subscription needed.
                  </div>
                </div>
              </div>
            ) : billing.active ? (
              billing.cancel_at_period_end ? (
                // CANCELLED — keeps access until period_end
                <div className="rounded-xl bg-[#C17767]/10 border border-[#C17767]/20 p-4 space-y-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#C17767] font-semibold">Cancellation scheduled</div>
                    <div className="mt-1 text-sm">
                      You&apos;ll keep access until <span className="font-medium" data-testid="settings-access-until">{fmtDate(billing.subscription_until)}</span>. No further charges.
                    </div>
                  </div>
                  <Button
                    onClick={handleResume}
                    disabled={resumeBusy}
                    data-testid="settings-resume-btn"
                    className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
                  >
                    <ArrowClockwise size={16} className="mr-1.5" weight="bold" />
                    {resumeBusy ? "Resuming…" : "Resume subscription"}
                  </Button>
                </div>
              ) : (
                // ACTIVE — show renewal date + cancel button
                <div className="rounded-xl bg-[#2C4C3B]/8 border border-[#2C4C3B]/15 p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#2C4C3B] font-semibold">Active</div>
                    <div className="mt-1">
                      Auto-renews on <span className="font-medium" data-testid="settings-renew-date">{fmtDate(billing.subscription_until)}</span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setConfirmingCancel(true)}
                    variant="outline"
                    className="rounded-full border-[#C17767]/40 text-[#C17767] hover:bg-[#C17767]/10 hover:text-[#C17767]"
                    data-testid="settings-cancel-btn"
                  >
                    <X size={16} className="mr-1.5" weight="bold" />
                    Cancel subscription
                  </Button>
                </div>
              )
            ) : (
              <div className="rounded-xl bg-[#C17767]/10 border border-[#C17767]/20 p-4 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#C17767] font-semibold">Inactive</div>
                  <div className="mt-1 text-sm">Subscribe to log new trips and use AI receipt scanning.</div>
                </div>
                <Button onClick={() => navigate("/subscribe")} className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white" data-testid="settings-resubscribe">
                  See plans
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmingCancel} onOpenChange={setConfirmingCancel}>
        <AlertDialogContent data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your MedEasy subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll keep full access until <strong>{fmtDate(billing?.subscription_until)}</strong>. No further charges will be made. You can resume anytime before that date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-no">Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={cancelBusy}
              data-testid="cancel-confirm-yes"
              className="bg-[#C17767] hover:bg-[#A85F50] text-white"
            >
              {cancelBusy ? "Cancelling…" : "Yes, cancel"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="rounded-2xl border-[#E3E1D9]">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#C17767] flex items-center justify-center">
              <AirplaneTakeoff weight="duotone" size={20} className="text-white" />
            </div>
            <div>
              <h2 className="font-[Manrope] font-semibold text-lg">Hope Air contribution</h2>
              <p className="text-sm text-[#5C6660]">10% of every payment to MedEasy supports Hope Air.</p>
            </div>
          </div>
          {stats && false && (
            <div className="rounded-xl bg-[#EBE9E0]/50 p-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold">MedEasy community total</div>
                <div className="font-[Manrope] font-bold text-2xl text-[#C17767] mt-1">{fmtCAD(stats.hope_air_donated_cad)}</div>
              </div>
              <Button variant="outline" onClick={() => navigate("/hope-air")} className="rounded-full border-[#E3E1D9]" data-testid="settings-hope-air-link">
                Learn more <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit backup — bulk receipt download */}
      {isAuthed && (
        <Card className="rounded-2xl border-[#E3E1D9]" data-testid="audit-backup-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center shrink-0">
                <FileZip size={20} weight="duotone" className="text-white" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6660] font-semibold">
                  Your audit backup · 6-year CRA window
                </div>
                <h2 className="font-[Manrope] font-semibold text-lg mt-0.5">Download all my receipts</h2>
                <p className="text-sm text-[#5C6660] mt-2 leading-relaxed">
                  Bundles every receipt photo + a <code className="text-[11px] bg-[#F7F6F2] px-1 rounded">manifest.csv</code> linking each one to its trip or prescription. We keep your data backed up while your account is active, <strong className="text-[#1E2420]">but your tax records are too important to rely on any single service</strong> — including ours. Download this archive once a year and store it somewhere you control (cloud drive, external disk, or with your accountant) for the CRA&apos;s 6-year retention window.
                </p>

                {/* Optional AES-256 password */}
                <div className="mt-4 rounded-xl border border-[#E3E1D9] bg-[#FAF9F4] p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Lock size={14} weight="duotone" className="text-[#2C4C3B]" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#2C4C3B] font-semibold">
                      Optional: AES-256 encrypt this archive
                    </span>
                  </div>
                  <p className="text-xs text-[#5C6660] leading-relaxed">
                    For extra peace of mind, set a password. Receipts will be encrypted with AES-256 so they&apos;re unreadable to anyone without your passphrase — even if your cloud backup is compromised. <strong>We don&apos;t store your password</strong>, so if you forget it the file cannot be recovered.
                  </p>
                  <div className="relative mt-3 max-w-md">
                    <input
                      data-testid="zip-password-input"
                      type={showZipPwd ? "text" : "password"}
                      value={zipPassword}
                      onChange={(e) => setZipPassword(e.target.value)}
                      placeholder="Leave blank for plain ZIP · min 8 chars to encrypt"
                      autoComplete="new-password"
                      className="w-full h-10 pl-3 pr-10 rounded-lg border border-[#E3E1D9] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30"
                    />
                    <button
                      type="button"
                      data-testid="zip-password-toggle"
                      onClick={() => setShowZipPwd((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C6660] hover:text-[#1E2420] p-1"
                      aria-label={showZipPwd ? "Hide password" : "Show password"}
                    >
                      {showZipPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {zipPassword && zipPassword.length > 0 && zipPassword.length < 8 && (
                    <div className="text-[11px] text-[#C17767] mt-1.5" data-testid="zip-password-warning">
                      Need at least 8 characters — or clear the field to skip encryption.
                    </div>
                  )}
                </div>
              </div>
              <Button
                data-testid="settings-download-zip"
                onClick={handleDownloadReceiptsZip}
                disabled={zipBusy}
                className="rounded-full bg-[#2C4C3B] hover:bg-[#243D2F] text-white h-11 px-5"
              >
                {zipPassword ? (
                  <Lock size={18} weight="duotone" className="mr-2" />
                ) : (
                  <FileZip size={18} weight="duotone" className="mr-2" />
                )}
                {zipBusy ? "Preparing ZIP…" : (zipPassword ? "Download encrypted ZIP" : "Download ZIP")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-[#E3E1D9]">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#1E2420] flex items-center justify-center">
              <UserIcon weight="duotone" size={20} className="text-white" />
            </div>
            <h2 className="font-[Manrope] font-semibold text-lg">Account</h2>
          </div>
          {isAuthed ? (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#2C4C3B] text-white flex items-center justify-center font-medium">{user?.name?.[0] || "U"}</div>
                )}
                <div>
                  <div className="font-medium" data-testid="settings-account-name">{user?.name}</div>
                  <div className="text-sm text-[#5C6660]" data-testid="settings-account-email">{user?.email}</div>
                </div>
              </div>
              <Button data-testid="settings-logout" onClick={logout} variant="outline" className="rounded-full border-[#E3E1D9]">Sign out</Button>
            </div>
          ) : (
            <Button onClick={goLogin} className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white" data-testid="settings-signin-cta">
              Sign in to MedEasy
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Privacy & Legal — discreet footer block so users can revisit our
          Privacy Policy and Terms from inside the app without leaving to
          the marketing site. Sits below Account on purpose: visible if
          you look for it, not in the way of daily logbook use. */}
      <div
        data-testid="settings-legal-footer"
        className="pt-2 pb-8 text-center text-xs text-[#5C6660] space-y-2"
      >
        <div className="font-[Manrope] tracking-[0.18em] uppercase text-[10px] text-[#5C6660]/80">
          Privacy &amp; Legal
        </div>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <button
            onClick={() => navigate("/privacy")}
            data-testid="settings-privacy-link"
            className="hover:text-[#1E2420] underline underline-offset-2"
          >
            Privacy Policy
          </button>
          <span className="text-[#E3E1D9]">·</span>
          <button
            onClick={() => navigate("/terms")}
            data-testid="settings-terms-link"
            className="hover:text-[#1E2420] underline underline-offset-2"
          >
            Terms of Service
          </button>
          <span className="text-[#E3E1D9]">·</span>
          <a
            href="mailto:support@MyMedEasy.ca"
            data-testid="settings-contact-link"
            className="hover:text-[#1E2420] underline underline-offset-2"
          >
            Contact support
          </a>
        </div>
        <div className="text-[#5C6660]/70 pt-1">
          © {new Date().getFullYear()} 10203786 Manitoba Ltd. operating as MedEasy · Built in Canada
        </div>
      </div>
    </div>
  );
}
